import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const navigate = useNavigate();

  const scrollToMethodology = () => {
    const methodologySection = document.getElementById('proven-methodologies');
    if (methodologySection) {
      methodologySection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const navigateToServices = () => {
    navigate('/services-overview-interactive-capability-showcase');
  };

  return (
    <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary/20 text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
        <div className="absolute top-40 right-20 w-24 h-24 border border-white/20 rounded-full"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 border border-white/20 rounded-full"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* North Star Metaphor */}
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                <Icon name="Navigation" size={24} className="text-secondary compass-rotate" />
              </div>
              <span className="text-secondary font-semibold text-lg">Your Data Navigation Guide</span>
            </div>

            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Your Trusted Data Partner.
                <span className="text-secondary block">From Chaos to Clarity.</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed">
                Like the North Star that has guided explorers for centuries, NorthStar Analytics serves as your fixed point of reference in the vast ocean of data complexity, guiding your journey from data chaos to strategic clarity.
              </p>

              <div className="bg-white/10 rounded-xl p-6 border border-white/20">
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="font-semibold text-secondary">Mission</div>
                    <p className="text-white/90">Help organizations build data maturity through clear strategy, tailored implementation, and sustainable adoption.</p>
                  </div>
                  <div className="space-y-2">
                    <div className="font-semibold text-secondary">About</div>
                    <p className="text-white/90">The go-to boutique consultancy in Ireland and the EU for pragmatic, business-driven data solutions.</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-white/80 leading-relaxed">
                We position ourselves as the trusted partner for mid-sized companies and institutions seeking to modernize their data ecosystems without the overhead of large consulting firms—more agile than Big 4 consultancies, yet more credible than freelancers.
              </p>
            </div>

            {/* Key Differentiators */}
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-secondary">Ireland & EU</div>
                <div className="text-white/80">Boutique Consultancy Focus</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-secondary">SME+</div>
                <div className="text-white/80">Mid-Cap Specialization</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-secondary">Education-First</div>
                <div className="text-white/80">Sustainable Adoption</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="secondary"
                size="lg"
                iconName="Users"
                iconPosition="left"
                className="bg-secondary hover:bg-secondary/90 text-white"
                onClick={navigateToServices}
              >
                Our Services
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="FileText"
                iconPosition="left"
                className="border-white/30 text-white hover:bg-white/10"
                onClick={scrollToMethodology}
              >
                Our Methodology
              </Button>
            </div>
          </div>

          {/* Visual */}
          <div className="relative">
            <div className="relative z-10">
              <Image
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Data strategy consultation meeting"
                className="rounded-2xl shadow-2xl w-full h-96 lg:h-[500px] object-cover"
              />
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} className="text-secondary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">Data Chaos to Strategic Clarity</div>
                    <div className="text-sm text-muted-foreground">Sustainable Data Success</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Background Decoration */}
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-secondary/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;